import { injectable, inject, multiInject } from 'inversify'
import { makeObservable, action, observable, runInAction } from 'mobx'
import { capabilitySchema, type Capability } from '@shared/index'
import { UserModel } from './user.model'
import { HttpGateway } from '@core/services/http.gateway'
import { SessionStorageGateway } from '@core/services/session-storage.gateway'
import { MessagePacking, type ActionFailurePm, type ActionResponsePm } from '@core/feedback/feedback.packing'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import { OfflineCacheRepository } from '@core/infra/offline/offline-cache.repository'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationResult } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

const SESSION_KEY = 'the-duke.session'

// The role values come from the shared schema so backend and frontend cannot
// drift; it is required in every session-bearing DTO — a backend that stops
// sending it fails parsing loudly (invalid-external-data) instead of quietly
// downgrading everyone's UI. A pre-role persisted session fails the stored
// shape the same way and simply asks for a fresh login.
const RoleSchema = z.enum(['user', 'admin'])
const StoredSessionSchema = z.object({ email: z.string(), token: z.string().min(1), role: RoleSchema })
const LoginDtoSchema = z.object({ token: z.string().min(1), role: RoleSchema })
const RegistrationDtoSchema = z.object({ message: z.string() })
const RefreshDtoSchema = z.object({ token: z.string().min(1), role: RoleSchema })
/**
 * Capabilities are validated against the shared vocabulary, but unknown values
 * are dropped rather than failing the whole response: a server that has learnt
 * a new capability this bundle has not must not log the user out.
 */
const MembershipDtoSchema = z.object({
  workspaceId: z.string(),
  name: z.string(),
  role: z.enum(['owner', 'manager', 'member', 'guest']),
  personal: z.boolean(),
})

const IdentityDtoSchema = z.object({
  email: z.string(),
  role: RoleSchema,
  capabilities: z
    .array(z.string())
    .transform((values) =>
      values.filter((value): value is Capability => (capabilitySchema.options as readonly string[]).includes(value)),
    ),
  // Defaulted: an older server that predates workspace membership (plan slice
  // 6) simply has none, which is not a reason to fail the identity load.
  memberships: z.array(MembershipDtoSchema).default([]),
  activeWorkspaceId: z.string().nullable().default(null),
  // Defaulted for the same reason as `memberships` — an older server that
  // predates the play-phase tip simply hasn't dismissed it, which is not a
  // reason to fail the identity load.
  hasSeenPlayTip: z.boolean().default(false),
})
const GuestDtoSchema = z.object({
  email: z.string(),
  token: z.string().min(1),
  role: RoleSchema,
  // Echoed back rather than trusted verbatim from the request — the server
  // may sanitise/truncate it, and this is what UserModel.displayName ends up
  // showing everywhere (including to the opponent in a lobby).
  name: z.string().min(1),
})
// Same shape as the guest payload plus the one job the link is for, so the
// exchange page can navigate straight to it.
const ShareLinkSessionDtoSchema = z.object({
  email: z.string(),
  token: z.string().min(1),
  role: RoleSchema,
  jobId: z.string().min(1),
})

// `ignore`, not `join`: a second concurrent login/register/guest submit while
// one is pending is almost always a double-click on the same form, and
// rejecting it outright is simpler and safer than joining mismatched credential
// intent under one shared execution (spec §7.1's key-semantics warning).
const AUTHENTICATION_GROUP = operationGroup('authentication')

// A timeout here does not prove the credential/registration write never
// reached the server — it only stops the submit button from being stuck
// forever. No retry on any of the three regardless (never safe on a
// non-idempotent write); 10s matches authentication.refresh below, since
// these are the same kind of auth-protocol POST.
const AUTHENTICATION_WRITE_TIMEOUT = {
  kind: 'after' as const,
  milliseconds: 10_000,
  toFailure: () => ({ code: 'temporarily-unavailable' as const }),
}

export const LOGIN_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.login'),
  key: () => operationKey('login'),
  groups: () => [AUTHENTICATION_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: AUTHENTICATION_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const REGISTER_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.register'),
  key: () => operationKey('register'),
  groups: () => [AUTHENTICATION_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: AUTHENTICATION_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

export const GUEST_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.guest'),
  key: () => operationKey('guest'),
  groups: () => [AUTHENTICATION_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: AUTHENTICATION_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Exchanging a job share-link token for a session — the outsider entry point
// (backend routes/share.routes.ts). Same posture as GUEST_OPERATION: a
// non-idempotent auth POST, never retried, one at a time.
export const SHARE_LINK_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.share-link'),
  key: () => operationKey('share-link'),
  groups: () => [AUTHENTICATION_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'non-idempotent-write' },
  timeout: AUTHENTICATION_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// `join`, not `ignore`: concurrent 401s from several in-flight requests must
// share one refresh, not have all but the first silently dropped. This is
// what HttpGateway's own `refreshInFlight` used to dedupe — that ownership
// now lives here (spec §21 Phase 1 step 11). Retry stays `none` permanently:
// this is authentication protocol, not a generic transient-failure retry.
export const REFRESH_TOKEN_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.refresh'),
  key: () => operationKey('session'),
  groups: () => [operationGroup('session'), AUTHENTICATION_GROUP],
  pending: { kind: 'join' },
  safety: { kind: 'non-idempotent-write' },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

// Idempotent (dismissing twice is a no-op server-side) — safe to retry, unlike
// every other write in this file.
export const DISMISS_PLAY_TIP_OPERATION = defineOperation<null, ActionFailurePm>({
  id: operationId('authentication.dismiss-play-tip'),
  key: () => operationKey('dismiss-play-tip'),
  groups: () => [AUTHENTICATION_GROUP],
  pending: { kind: 'ignore' },
  safety: { kind: 'idempotent-write' },
  timeout: AUTHENTICATION_WRITE_TIMEOUT,
  retry: { kind: 'none' },
  debounce: { kind: 'none' },
})

/**
 * Source of truth for the signed-in session: log in/out, register, guest
 * access, and token refresh. Session identity itself lives on the separate
 * `UserModel` (not a `.repository.ts`), which this class reads and writes —
 * it is the one Repository that performs `resetUserScopedState` resets on
 * every other participant, rather than being reset itself.
 */
@injectable()
export class AuthenticationRepository {
  @inject(Types.IDataGateway)
  dataGateway!: HttpGateway

  @inject(Types.ISessionStorageGateway)
  storageGateway!: SessionStorageGateway

  @inject(UserModel)
  userModel!: UserModel

  @inject(OfflineCacheRepository)
  private offlineCache!: OfflineCacheRepository

  /**
   * Everything registered as holding user-scoped state, resolved as a group.
   * Every logout path funnels through logOut() below — the Logout menu, the
   * login page's own logOut, and a failed token refresh — so this is the one
   * place that can reliably invalidate it.
   *
   * Multi-injected rather than naming each repository: this file previously
   * listed BooksRepository and AssetsRepository by hand, which meant adding a
   * feature required editing auth to avoid leaking the previous user's data.
   * Registration now lives in the composition root. See core/interfaces/user-scoped-state.ts.
   */
  @multiInject(Types.IUserScopedState)
  private userScopedState!: UserScopedState[]

  private readonly operations: OperationRepository
  private readonly loginOperation: OperationHandle<null, ActionFailurePm>
  private readonly registerOperation: OperationHandle<null, ActionFailurePm>
  private readonly guestOperation: OperationHandle<null, ActionFailurePm>
  private readonly shareLinkOperation: OperationHandle<null, ActionFailurePm>
  private readonly refreshOperation: OperationHandle<null, ActionFailurePm>
  private readonly dismissPlayTipOperation: OperationHandle<null, ActionFailurePm>

  /**
   * Set when the session ended on its own — a token refresh failed — as opposed
   * to the user choosing to log out. AppPresenter reacts to it to explain what
   * happened and get the user off a page they can no longer load. A deliberate
   * logout leaves this false: the user knows why they are on the login page.
   */
  sessionExpired = false

  constructor(@inject(OperationRepository) operationRepository: OperationRepository) {
    this.operations = operationRepository
    const owner = operationRepository.createOwner({ id: 'authentication.repository', lifetime: 'application' })
    this.loginOperation = owner.register(LOGIN_OPERATION)
    this.registerOperation = owner.register(REGISTER_OPERATION)
    this.guestOperation = owner.register(GUEST_OPERATION)
    this.shareLinkOperation = owner.register(SHARE_LINK_OPERATION)
    this.refreshOperation = owner.register(REFRESH_TOKEN_OPERATION)
    this.dismissPlayTipOperation = owner.register(DISMISS_PLAY_TIP_OPERATION)

    makeObservable(this, {
      sessionExpired: observable,
      login: action,
      refreshToken: action,
      clearSessionExpired: action,
    })
  }

  clearSessionExpired = () => {
    this.sessionExpired = false
  }

  // Set after awaiting the refresh call, i.e. outside refreshToken's own action.
  private markSessionExpired = action(() => {
    this.sessionExpired = true
  })

  /**
   * Rehydrate the session persisted in sessionStorage (survives reload,
   * dies with the tab). Called once at bootstrap, before routes resolve,
   * so the auth guard sees the token on the very first navigation.
   */
  restoreSession = () => {
    // Wired here (not the constructor — property injections aren't populated
    // yet then) so HttpGateway can call back into a 401-triggered refresh
    // without depending on AuthenticationRepository itself (circular DI).
    this.dataGateway.refreshHandler = this.refreshToken

    // Unparseable data is dropped by the gateway; this still has to check the
    // shape, since valid JSON is not the same thing as a valid session.
    const stored = this.storageGateway.getValue(SESSION_KEY)
    const session = StoredSessionSchema.safeParse(stored)
    if (session.success) {
      this.userModel.setSession(session.data.email, session.data.token, session.data.role)
      // A restored session's stored role may be stale; its capabilities are
      // never stored, always fetched.
      void this.loadIdentity()
    }
  }

  /**
   * Fetch what this session may do. Called wherever a session begins — login,
   * register-then-login, guest, a restored session, and after a refresh —
   * rather than riding along on the login payload, so a client never gates its
   * UI on a stale capability list.
   *
   * Fire-and-forget by design: an identity that has not landed yet simply
   * offers nothing gated, which is the safe direction to fail. Nothing here
   * grants access — every route re-checks server-side.
   */
  loadIdentity = async (workspaceId?: string): Promise<void> => {
    const path = workspaceId === undefined ? '/me' : `/me?workspaceId=${encodeURIComponent(workspaceId)}`
    const response = await this.dataGateway.get(path, { signal: null, idempotencyKey: null })

    // Only the unscoped identity is cached — a workspace-scoped re-fetch is a
    // deliberate context switch that has no meaning offline.
    const dto = response.success
      ? IdentityDtoSchema.safeParse(response.result)
      : workspaceId === undefined
        ? this.cachedIdentity(response.failure)
        : null
    if (dto === null || !dto.success) return

    if (response.success) this.offlineCache.write('identity', response.result)

    runInAction(() => {
      this.userModel.setCapabilities(dto.data.capabilities)
      this.userModel.setWorkspaceContext(
        dto.data.memberships.map((membership) => ({ ...membership })),
        dto.data.activeWorkspaceId,
      )
      this.userModel.setHasSeenPlayTip(dto.data.hasSeenPlayTip)
    })
  }

  /** The last identity seen on this device, re-validated — served only when the device is offline. */
  private cachedIdentity(failure: Parameters<OfflineCacheRepository['readOnNetworkFailure']>[2]) {
    const cached = this.offlineCache.readOnNetworkFailure('identity', IdentityDtoSchema, failure)
    return cached === null ? null : { success: true as const, data: cached }
  }

  /**
   * Switch the active workspace: re-fetch the identity scoped to it, so the
   * capability list the UI gates on reflects the new workspace role. Nothing
   * about authorization changes — every write still derives its workspace from
   * the target server-side (plan slice 6, decision A).
   */
  switchWorkspace = async (workspaceId: string): Promise<void> => {
    await this.loadIdentity(workspaceId)
  }

  /**
   * One-way — there is no "un-dismiss". Sets the model field optimistically
   * on success rather than re-fetching identity, same as `login`'s own
   * `setSession` — the server's own idempotent write can't disagree with it.
   */
  dismissPlayTip = async (): Promise<ActionResponsePm> => {
    const result = await this.dismissPlayTipOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post('/me/dismiss-play-tip', {}, { signal, idempotencyKey: null })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }
        return operationSucceeded(null)
      },
      {
        succeeded: () => {
          this.userModel.setHasSeenPlayTip(true)
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  private persistSession = () => {
    this.storageGateway.setValue(SESSION_KEY, {
      email: this.userModel.email,
      token: this.userModel.token,
      role: this.userModel.role,
    })
  }

  login = async (email: string, password: string): Promise<ActionResponsePm> => {
    const result = await this.loginOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post('/login', { email, password }, { signal, idempotencyKey: null })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }

        const dto = LoginDtoSchema.safeParse(response.result)
        if (!dto.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(dto.data)
      },
      {
        succeeded: ({ token, role }) => {
          this.userModel.setSession(email, token, role)
          this.persistSession()
          void this.loadIdentity()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  register = async (email: string, password: string, inviteCode: string): Promise<ActionResponsePm> => {
    const result = await this.registerOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          '/register',
          { email, password, inviteCode },
          { signal, idempotencyKey: null },
        )
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }

        if (!RegistrationDtoSchema.safeParse(response.result).success) {
          return operationFailed({ code: 'invalid-external-data' })
        }

        return operationSucceeded(null)
      },
      { succeeded: null, failed: null },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * Called by HttpGateway on a 401 (not from /login or /refresh itself).
   * Returns whether the retry should go ahead; false means the caller's
   * request should surface as a normal failure.
   *
   * A 401 with no token at all (e.g. an AppLifecycle poll ticking on the
   * login page before anyone has signed in) means "never signed in", not
   * "signed in and expired" — posting /refresh anyway would fail and raise
   * the wrong "session expired" message for someone who never had one.
   */
  refreshToken = async (): Promise<boolean> => {
    if (!this.userModel.token) return false

    const result = await this.refreshOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post(
          '/refresh',
          { token: this.userModel.token },
          { signal, idempotencyKey: null },
        )
        if (response.success) {
          const dto = RefreshDtoSchema.safeParse(response.result)
          if (dto.success) return operationSucceeded(dto.data)
        }
        return operationFailed({ code: 'temporarily-unavailable' })
      },
      {
        succeeded: ({ token, role }) => {
          this.userModel.setSession(this.userModel.email, token, role)
          void this.loadIdentity()
          this.persistSession()
        },
        failed: null,
      },
    )

    if (result.kind === 'succeeded') return true

    // 'cancelled'/'skipped' means something else already resolved the session
    // transition (e.g. a deliberate logout cancelled the session group this
    // refresh belongs to) — reacting again here would show a wrong "session
    // expired" message over a logout the user asked for.
    if (result.kind === 'failed') {
      await this.logOut()
      this.markSessionExpired()
    }
    return false
  }

  /**
   * A real product path, not a dev bypass — but with a real token. This used
   * to fabricate 'guest-token' locally, which opened secure routes
   * client-side while every request the page then made was rejected by the
   * server. The guest now gets a genuine token from /guest and walks the same
   * auth path as anyone else, in every environment including production.
   */
  enterAsGuest = async (name: string): Promise<ActionResponsePm> => {
    const result = await this.guestOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post('/guest', { name }, { signal, idempotencyKey: null })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }

        const dto = GuestDtoSchema.safeParse(response.result)
        if (!dto.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(dto.data)
      },
      {
        succeeded: (guest) => {
          this.userModel.setSession(guest.email, guest.token, guest.role)
          this.userModel.setDisplayName(guest.name)
          void this.loadIdentity()
          this.persistSession()
        },
        failed: null,
      },
    )

    return this.toActionResponsePm(result)
  }

  /**
   * Exchange a job share-link token for a session. Returns the ActionResponsePm
   * so the exchange page can navigate on success or show an "invalid link"
   * state on failure. The response shape is identical to /guest and /login, so
   * everything downstream (persisted session, 90s access token, /refresh) works
   * for an 'external' identity with no branching — see the backend route.
   */
  exchangeShareLink = async (token: string): Promise<{ pm: ActionResponsePm; jobId: string | null }> => {
    let jobId: string | null = null
    const result = await this.shareLinkOperation.run(
      null,
      async ({ signal }) => {
        const response = await this.dataGateway.post('/share-link/session', { token }, { signal, idempotencyKey: null })
        if (!response.success) {
          const pm = MessagePacking.unpackGatewayResponseToPm(response)
          return operationFailed(pm.success ? { code: 'temporarily-unavailable' } : pm.failure)
        }

        const dto = ShareLinkSessionDtoSchema.safeParse(response.result)
        if (!dto.success) return operationFailed({ code: 'invalid-external-data' })

        return operationSucceeded(dto.data)
      },
      {
        succeeded: (session) => {
          this.userModel.setSession(session.email, session.token, session.role)
          void this.loadIdentity()
          this.persistSession()
          jobId = session.jobId
        },
        failed: null,
      },
    )

    return { pm: this.toActionResponsePm(result), jobId }
  }

  logOut = action(() => {
    // Revoke every session-tagged Operation's authority to publish *before*
    // identity/PM change — otherwise a pre-logout load settling afterward
    // could repopulate a Singleton Repository with the previous user's data
    // (spec §16).
    this.operations.resetGroup(operationGroup('session'), { kind: 'session-reset' })

    // One outer action makes the identity change and every participant reset a
    // single observable publication. Reactions cannot render "logged out"
    // beside the previous user's feature state between these nested actions.
    this.userModel.clearSession()
    this.storageGateway.remove(SESSION_KEY)

    // Loaded data is user-scoped and repositories are singletons that outlive
    // the session — without this the next user to log in would see the previous
    // user's data until a refetch landed. Identity change is the only reason
    // any of this is reset; leaving a route deliberately keeps its data.
    this.userScopedState.forEach((participant) => participant.resetUserScopedState())
  })

  private toActionResponsePm(result: OperationResult<unknown, ActionFailurePm>): ActionResponsePm {
    switch (result.kind) {
      case 'succeeded':
        return MessagePacking.succeeded()
      case 'failed':
        return { success: false, failure: result.error }
      case 'cancelled':
      case 'skipped':
        return MessagePacking.cancelled()
    }
  }
}
